import ApiErrorBanner from '@/components/ApiErrorBanner';
import { samplesConfigObj } from '@/components/Glossary/Sections/SampleTypes/SamplesConfig';
import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import Button from '@/components/UI/Button';
import { DateTimePickerNative } from '@/components/UI/DateTimePickerNative/DateTimePickerNative';
import type { MetadataFieldBuilderReturn } from '@/components/UI/FormFields/FormField.utils';
import { mapMetadataSubmissions, metadataFieldBuilder } from '@/components/UI/FormFields/FormField.utils';
import Timepoint from '@/components/UI/FormFields/Latest/Timepoint';
import FormRender from '@/components/UI/FormRender';
import { _isEmptyString, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { GlossaryItem } from '@/model/Glosary.model';
import type { MetadataFieldTypes, MetadataFieldWithValue } from '@/model/Metadata.model';
import type { Sample, SampleMeasurement, SampleMeasurementKey } from '@/model/Sample.model';
import { useRequest } from '@/support/Hooks/request';
import { ApiService } from '@/support/ApiService.ts';
import { featuresSelector } from '@/support/Selectors';
import { isMacPlatform } from '@/utils/browser.utils';
import { DateUtils } from '@/utils/Date.utils';
import { parseDuration } from '@/utils/DurationDsl';
import { ErrorMessage } from '@hookform/error-message';
import { createSelector } from '@reduxjs/toolkit';
import type { FC, KeyboardEvent, RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { serialize as durationSerialize } from 'tinyduration';
import { DetailField } from './DetailField';

const MAX_COMMENT_LENGTH = 255;

const FOCUSABLE_SELECTOR = 'input, select, textarea, button, a[href], [tabindex]';

/** Input types where selecting the content lets the next keystroke overwrite it. */
const SELECTABLE_INPUT_TYPES = new Set(['text', 'search', 'url', 'tel', 'password', 'number']);

const focusAndSelectInput = (input: HTMLElement | null): void => {
  if (_isNil(input)) {
    return;
  }
  input.focus();
  if (input instanceof HTMLInputElement && SELECTABLE_INPUT_TYPES.has(input.type)) {
    input.select();
  }
};

/**
 * Rendered-and-visible check for focus candidates. Zero-size elements only count as hidden when the
 * environment actually performs layout, since jsdom reports every rect as empty.
 */
const isElementVisible = (element: HTMLElement, measureLayout: boolean): boolean => {
  if (element.hidden === true) {
    return false;
  }

  if (measureLayout) {
    const { width, height } = element.getBoundingClientRect();
    if (width === 0 && height === 0) {
      return false;
    }
  }

  let node: HTMLElement | null = element;
  while (_notNil(node)) {
    const { display, visibility } = window.getComputedStyle(node);
    if (display === 'none' || visibility === 'hidden') {
      return false;
    }
    node = node.parentElement;
  }

  return true;
};

const MULTI_SELECT_FIELD_TYPES = new Set<MetadataFieldTypes>(['multi_select', 'lookup_multi_select']);

/**
 * True when Enter is being consumed by a multi-select whose menu is open. The select commits the
 * highlighted option itself, so the form must not also advance — focus stays on the field, and the
 * next Enter (or Escape then Enter) moves on once the menu is closed.
 *
 * Detection uses the WAI-ARIA combobox contract (`role` / `aria-expanded`) plus the `FormField`
 * wrapper's field key, so no select-library internals are relied on.
 */
const isOpenMultiSelectMenu = (element: HTMLElement | null, multiSelectFieldNames: Set<string>): boolean => {
  if (_isNil(element)) {
    return false;
  }
  if (element.getAttribute('role') !== 'combobox' || element.getAttribute('aria-expanded') !== 'true') {
    return false;
  }

  const fieldName = element
    .closest<HTMLElement>('[data-test-component="FormField"][data-test-key]')
    ?.getAttribute('data-test-key');

  return _notNil(fieldName) && multiSelectFieldNames.has(fieldName);
};

/**
 * Every focusable control inside the form, in DOM order — effectively the Tab sequence.
 * Enter walks this list one stop at a time so nothing is skipped (unit selects, metadata fields and
 * the footer buttons are all stops), ending on the submit button where a final Enter saves.
 */
const getEnterNavigationFields = (form: HTMLFormElement): Array<HTMLElement> => {
  const measureLayout = document.body.getBoundingClientRect().height > 0;

  return Array.from(form.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).reduce<Array<HTMLElement>>(
    (fields, element) => {
      if (element.hasAttribute('disabled') || element.getAttribute('tabindex') === '-1') {
        return fields;
      }
      if (element instanceof HTMLInputElement && element.type === 'hidden') {
        return fields;
      }
      if (!isElementVisible(element, measureLayout)) {
        return fields;
      }
      fields.push(element);
      return fields;
    },
    []
  );
};

const plannedSamplesEnabledSelector = createSelector(
  [featuresSelector],
  (features): boolean => features?.planned_samples ?? false
);

const sampleNamingTemplatesEnabledSelector = createSelector(
  [featuresSelector],
  (features): boolean => features?.sample_naming_templates ?? false
);

interface InitialSample {
  sample_id?: string;
  date?: string;
  timepoint?: string;
  metadata: unknown;
  meta?: Record<string, unknown>;
  details: Array<SampleMeasurement>;
  comments?: string;
  reason_for_change?: string;
}

const initialiseSample = (sampleType: GlossaryItem, sample?: Sample): InitialSample => {
  const typeDetails = (sampleType?.options?.details ?? [])
    .filter((detailKey): detailKey is SampleMeasurementKey =>
      ['weight', 'width', 'length', 'depth', 'volume'].includes(detailKey)
    )
    .map<SampleMeasurement>((detailKey) => ({
      key: detailKey,
      unit: (samplesConfigObj?.[detailKey]?.units?.[0] ?? '') as SampleMeasurement['unit'],
      value: '' as SampleMeasurement['value'],
    }));

  if (_notNil(sample)) {
    const sampleDetailsMap = new Map<SampleMeasurement['key'], SampleMeasurement>(
      (sample?.details ?? []).map((detail) => [detail.key, detail])
    );

    return {
      sample_id: sample?.sample_id,
      details: typeDetails.map((detail) =>
        sampleDetailsMap.has(detail.key) ? (sampleDetailsMap.get(detail.key) as SampleMeasurement) : detail
      ),
      metadata: sample?.metadata,
      comments: sample?.comments,
      date: sample?.date ?? '',
      timepoint: _isNotBlank(sample?.timepoint)
        ? DateUtils.renderDuration(sample.timepoint, {
            format: 'short',
            zeros: true,
            defaultResponse: sample.timepoint,
          })
        : undefined,
    };
  }

  return {
    metadata: [],
    details: typeDetails,
    reason_for_change: '',
  };
};

const sampleMetadataFields = (
  sampleMetas: Array<MetadataFieldWithValue>,
  sample: InitialSample
): Array<MetadataFieldBuilderReturn> =>
  sampleMetas.map((meta: MetadataFieldWithValue) => metadataFieldBuilder(meta, sample));

interface SampleFormProps {
  animalId: ID;
  animalApiId: string;
  collectedAt: string;
  allowNaming: boolean;
  sample?: Sample;
  sampleType: GlossaryItem;
  sampleMetadataGlossary: Array<MetadataFieldWithValue>;
  handleAfterSave: (response: unknown) => void;
  saveButtonText: string;
  includeDatetimeField: boolean;
  formContainerRef: RefObject<HTMLFormElement | null>;
}

/**
 * Collect-sample form with keyboard navigation: autofocus the first focusable control, then Enter
 * steps through the form's focus order one control at a time, ending on the save button.
 */
export const SampleForm: FC<SampleFormProps> = ({
  animalId,
  animalApiId,
  collectedAt,
  allowNaming = false,
  sample,
  sampleType,
  sampleMetadataGlossary,
  handleAfterSave,
  saveButtonText = 'Save',
  includeDatetimeField = false,
  formContainerRef,
}) => {
  const plannedSamplesEnabled = useSelector(plannedSamplesEnabledSelector);
  const sampleNamingTemplatesEnabled = useSelector(sampleNamingTemplatesEnabledSelector);
  const unitDisabled = _notNil(sample?.sample_definition_api_id);
  const initialSample = initialiseSample(sampleType, sample);
  const submittingRef = useRef(false);
  const pendingCaretPositionRef = useRef<number | null>(null);
  const commentsTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const formMethods = useForm<InitialSample>({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    defaultValues: initialSample,
  });
  const {
    register,
    control,
    setValue,
    formState: { errors },
    handleSubmit,
  } = formMethods;
  const { fields: details } = useFieldArray<InitialSample, 'details'>({
    control,
    name: 'details',
  });
  const commentsValue = useWatch({ control, name: 'comments' });
  // FormRender spreads each descriptor into its field component, so this opts date metadata fields
  // into the ArrowDown picker shortcut for this form only.
  const metaFieldValues = sampleMetadataFields(sampleMetadataGlossary, initialSample).map((field) =>
    field.type === 'date' ? { ...field, openPickerOnArrowDown: true } : field
  );
  const multiSelectFieldNames = metaFieldValues.reduce<Set<string>>((names, field) => {
    if (MULTI_SELECT_FIELD_TYPES.has(field.type)) {
      names.add(field.name);
    }
    return names;
  }, new Set());
  const commentsRegister = register('comments', {
    maxLength: {
      value: MAX_COMMENT_LENGTH,
      message: 'Must be a maximum of 255 characters',
    },
  });

  const {
    sendRequest: checkIfNameIsUnique,
    requestSending: checkingIfNameIsUnique,
    requestError: nameValidationRequestError,
  } = useRequest<{ value: unknown }, { is_unique: boolean }>({
    route: 'samples.validate.isNameUnique',
    method: 'post',
  });

  const {
    sendRequest: saveSample,
    requestSending: savingSample,
    requestError: saveSampleError,
  } = useRequest({
    route: _notNil(sample) ? 'animal.sample.update' : 'animal.sample.create',
    params: { id: sample?.id ?? animalId },
    method: _notNil(sample) ? 'put' : 'post',
  });

  const {
    sendRequest: updateSample,
    requestSending: updatingSample,
    requestError: updateSampleError,
  } = useRequest({
    route: 'animal.sample.update',
    params: {
      id: sample?.id,
    },
    method: 'put',
  });

  const [creatingSample, setCreatingSample] = useState(false);
  const [createSampleError, setCreateSampleError] = useState<false | Error>(false);

  const validateNameIsUnique = async (value: string | undefined): Promise<true | string> => {
    if (sample?.sample_id !== value) {
      const response = await checkIfNameIsUnique({ value });
      return (response?.data?.is_unique ?? true) ? true : 'This name is already in use, please try another';
    } else {
      return true; // the sample name has already been reserved
    }
  };

  interface FormattedPayload {
    team_glossary_api_id: string;
    sample_id?: string;
    comments?: string;
    details: Array<SampleMeasurement>;
    date?: string | null;
    metadata: unknown;
    timepoint?: string | null;
    reason_for_change?: string;
  }

  const handlePayload = (data: InitialSample): FormattedPayload => {
    const formattedData: FormattedPayload = {
      team_glossary_api_id: sampleType.team_glossary_api_id,
      details:
        data.details?.reduce<Array<SampleMeasurement>>((acc, detail) => {
          const raw = detail.value;
          if (_isNil(raw) || _isEmptyString(String(raw))) {
            return acc;
          }
          const numericValue = Number(raw);
          if (!Number.isFinite(numericValue)) {
            return acc;
          }
          acc.push({ ...detail, value: numericValue as SampleMeasurement['value'] });
          return acc;
        }, []) ?? [],
      metadata: mapMetadataSubmissions(data.meta, sampleMetadataGlossary),
      comments: data.comments,
      reason_for_change: data.reason_for_change,
    };

    if (_notNil(sample)) {
      // UPDATE: Keep empty string if user cleared the input
      formattedData.date = data.date ?? null;
    } else {
      // CREATE: Use provided date or fall back to collectedAt if undefined/null
      formattedData.date = data.date ?? collectedAt;
    }

    // Handle timepoint differently for create vs update
    if (_notNil(sample)) {
      // UPDATE: Always include timepoint (null clears it, string sets it)
      formattedData.timepoint = _isNotBlank(data.timepoint) ? data.timepoint : null;
    } else {
      // CREATE: Only include timepoint if it has a value (omit if empty)
      if (_isNotBlank(data.timepoint)) {
        formattedData.timepoint = data.timepoint;
      }
    }

    if (allowNaming && _notNil(data?.sample_id)) {
      formattedData.sample_id = data.sample_id;
    }

    return formattedData;
  };

  const onSubmit = async (data: InitialSample): Promise<void> => {
    if (submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    try {
      if (_isNotBlank(data.timepoint)) {
        try {
          const duration = await parseDuration(data.timepoint);
          data.timepoint = durationSerialize(duration);
        } catch {
          // If conversion fails, validation should have caught it, but keep original value
        }
      }

      const payload = handlePayload(data);

      if (sampleNamingTemplatesEnabled) {
        if (_notNil(sample)) {
          const response = await updateSample(payload);
          handleAfterSave(response);
          return;
        }

        setCreateSampleError(false);
        setCreatingSample(true);
        try {
          const response = await ApiService.call({
            endpoint: 'POST /api/v1/animals/{animalId}/samples',
            path: { animalId: animalApiId },
            body: payload as never,
            options: { onError: { toast: false, throw: true, capture: true, slug: 'workflow-sample-create' } },
          });
          handleAfterSave(response.body);
        } catch (error) {
          setCreateSampleError(error as Error);
        } finally {
          setCreatingSample(false);
        }
      } else {
        const response = await saveSample(payload);
        handleAfterSave(response);
      }
    } finally {
      submittingRef.current = false;
    }
  };

  const disableSavePress =
    checkingIfNameIsUnique ||
    (sampleNamingTemplatesEnabled ? updatingSample || creatingSample : savingSample) ||
    (plannedSamplesEnabled && _notNil(errors.timepoint));

  const submitForm = handleSubmit(onSubmit);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      const form = formContainerRef.current;
      if (_isNil(form)) {
        return;
      }
      const firstField = getEnterNavigationFields(form)[0];
      focusAndSelectInput(firstField ?? null);
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [formContainerRef, allowNaming, plannedSamplesEnabled, details.length]);

  useLayoutEffect(() => {
    const caretPosition = pendingCaretPositionRef.current;
    if (_isNil(caretPosition)) {
      return;
    }
    pendingCaretPositionRef.current = null;
    commentsTextareaRef.current?.setSelectionRange(caretPosition, caretPosition);
  }, [commentsValue]);

  const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing === true || event.repeat) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    // Leave native activation for focused buttons/links.
    if (target.tagName === 'BUTTON' || target.tagName === 'A') {
      return;
    }

    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const form = formContainerRef.current;
    if (_isNil(form)) {
      return;
    }

    const navigationFields = getEnterNavigationFields(form);
    const isCommentsField = target.getAttribute('data-test-element') === 'comment-textarea';

    if (isCommentsField) {
      // Shift+Enter keeps native newline; Ctrl/Cmd+Enter inserts a newline manually.
      if (event.shiftKey === true) {
        return;
      }

      if (event.ctrlKey === true || event.metaKey === true) {
        event.preventDefault();
        event.stopPropagation();

        if (!(target instanceof HTMLTextAreaElement)) {
          return;
        }

        const { selectionStart, selectionEnd, value } = target;
        if (_isNil(selectionStart) || _isNil(selectionEnd)) {
          return;
        }

        const nextValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`;
        if (nextValue.length > MAX_COMMENT_LENGTH) {
          return;
        }

        pendingCaretPositionRef.current = selectionStart + 1;
        setValue('comments', nextValue, { shouldDirty: true, shouldValidate: true });
        return;
      }

      // Plain Enter falls through to the shared navigation path below (advances to the next control).
    }

    if (event.shiftKey === true || event.ctrlKey === true || event.metaKey === true) {
      return;
    }

    // An open multi-select menu owns Enter, so stay put instead of advancing.
    if (
      isOpenMultiSelectMenu(target, multiSelectFieldNames) ||
      isOpenMultiSelectMenu(activeElement, multiSelectFieldNames)
    ) {
      event.preventDefault();
      return;
    }

    const currentIndex = navigationFields.findIndex((field) => field === target || field === activeElement);
    if (currentIndex < 0) {
      // Block native Enter-submit from controls outside the navigation order.
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT') {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    // Enter advances through the navigation order instead of submitting; the final Enter lands on the
    // submit button, where the early BUTTON return lets the browser activate it exactly once.
    event.preventDefault();
    event.stopPropagation();

    const nextField = navigationFields[currentIndex + 1];
    if (_notNil(nextField)) {
      focusAndSelectInput(nextField);
    }
  };

  const newlineShortcutLabel = isMacPlatform() === true ? 'Cmd + Enter' : 'Ctrl + Enter';

  return (
    <FormProvider {...formMethods}>
      <form
        ref={formContainerRef}
        onSubmit={submitForm}
        onKeyDown={handleFormKeyDown}
        className="bl b--moon-gray min-h-100"
        data-test-component="SampleForm"
        data-test-element="form-container"
      >
        <>
          {_notNil(sampleType?.title) && <h3 className="lh-title f5 fw5 ph3 pt3">{sampleType.title}</h3>}
          {nameValidationRequestError !== false ||
          (sampleNamingTemplatesEnabled
            ? updateSampleError !== false || createSampleError !== false
            : saveSampleError !== false) ? (
            <ApiErrorBanner
              className="ma3"
              error={
                nameValidationRequestError ||
                (sampleNamingTemplatesEnabled ? updateSampleError || createSampleError : saveSampleError)
              }
            />
          ) : null}
          {includeDatetimeField && (
            <div className="pa3">
              <label htmlFor="date">Date collected</label>
              <Controller
                name="date"
                control={control}
                rules={{
                  required: sample?.status === 'collected' ? 'This field is required' : false,
                }}
                render={({ field: { value, onChange } }) => (
                  <DateTimePickerNative value={value} onChange={onChange} openPickerOnArrowDown />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="date"
                render={({ message }) => <p className="f6 red db pt2">{message}</p>}
              />
            </div>
          )}
          {allowNaming && (
            <div className="pa3">
              <label htmlFor="sample_id">Unique sample ID</label>
              <input
                id="sample_id"
                type="text"
                {...register('sample_id', {
                  required: 'This field is required',
                  minLength: {
                    value: 3,
                    message: 'Must be a minimum of 3 characters',
                  },
                  maxLength: {
                    value: 255,
                    message: 'Must be a maximum of 255 characters',
                  },
                  validate: {
                    validateNameIsUnique,
                  },
                })}
                style={{ marginBottom: 0 }}
                data-testid="sample-name-input"
              />
              <ErrorMessage
                errors={errors}
                name="sample_id"
                render={({ message }) => <p className="f6 red db pt2">{message}</p>}
              />
            </div>
          )}
          {_isNotEmpty(details) && (
            <div
              className="w-100 pa2 flex justify-between flex-wrap"
              style={{ maxWidth: '32rem' }}
              data-test-element="details-container"
            >
              {details.map((detailProps, fieldIndex) => (
                <DetailField
                  key={detailProps.id}
                  detailKey={detailProps.key ?? ''}
                  fieldIndex={fieldIndex}
                  value={detailProps.value?.toString()}
                  unit={detailProps.unit}
                  unitDisabled={unitDisabled}
                />
              ))}
            </div>
          )}
          {plannedSamplesEnabled && (
            <div className="pa3">
              <Timepoint name="timepoint" label="Timepoint" allow_zero allow_negative allowEnterPropagation />
            </div>
          )}
          <div className="pa3" data-test-element="comment-container">
            <label htmlFor="comments">Comment</label>
            <textarea
              id="comments"
              data-test-element="comment-textarea"
              data-testid="sample-comments"
              style={{ marginBottom: 0 }}
              aria-describedby="sample-comments-newline-hint"
              {...commentsRegister}
              ref={(element) => {
                commentsRegister.ref(element);
                commentsTextareaRef.current = element;
              }}
            />
            <p id="sample-comments-newline-hint" className="f7 gray lh-copy mb0 mt1">
              Press {newlineShortcutLabel} or Shift + Enter to start a new line
            </p>
          </div>
          {_isNotEmpty(metaFieldValues) && (
            <div className="ph3">
              <FormRender fields={metaFieldValues} />
            </div>
          )}
          {_notNil(sample) && (
            <div className="ph3 mb3">
              <ReasonForChange />
            </div>
          )}
          <div className="pa3 mb3" data-test-element="action-container">
            <Button submit disabled={disableSavePress}>
              {saveButtonText}
            </Button>
          </div>
        </>
      </form>
    </FormProvider>
  );
};

export default SampleForm;
